import {
    CodeParams,
    PhoneVerificationActions,
    PhoneVerificationSelectors,
} from 'access/modules/PhoneVerificationModule/PhoneVerificationModule';
import Button from 'mcs-toolkit/components/Button';
import React from 'react';
import { asyncHelper } from 'utils/asyncHelper';
import { McsErrors } from 'utils/McsErrors/McsErrors';
import { ReduxTypeUtils } from 'utils/ReduxTypeUtils';

import styles from 'reactApp/components/PhoneVerification/PhoneVerification.css';
import { GlobalState } from 'reactApp/core/rootReducer';
import { CurrentUserSelectors } from 'reactApp/modules/CurrentUserModule/CurrentUserModule';
import ButtonLink from 'reactComponents/buttonLink';
import Dialog from 'reactComponents/dialog/Dialog';
import { DialogAsMethodRedererProps } from 'reactComponents/DialogAsMethodRederer/DialogAsMethodRederer';
import { Forms } from 'reactComponents/Forms';
import { connectFormikProps } from 'reactComponents/Forms/ConnectFormikProps';

export type PhoneVerificationProps = {
    onConfirmed(): void;
    beforeConfirmed?(): void;
    likeDialog?: boolean;
    dialogDescription?: React.ReactNode;
    dialogHeader?: string;
    onCodeSent?(): void;
    phoneButtonText?: string;
    codeButtonText?: string;
    isRequesting?: boolean;
};

type Values = {
    phone: number | string;
    code?: number;
};
const connector = ReduxTypeUtils.makeConnector(
    (state: GlobalState) => ({
        sessionId: PhoneVerificationSelectors.getSessionId(state),
        phone: CurrentUserSelectors.getPhone(state),
    }),
    {
        phoneForward: PhoneVerificationActions.phoneForward,
        codeConfirm: PhoneVerificationActions.confirmCode,
    },
);
type ConnectedProps = ReduxTypeUtils.TypeOfConnector<typeof connector>;

const formikConnector = connectFormikProps((props: ConnectedProps) => {
    return {
        initialValues: {
            phone: props.phone || '',
        },
    };
});

type Props = PhoneVerificationProps &
    ConnectedProps &
    ReduxTypeUtils.TypeOfConnector<typeof formikConnector> &
    Partial<DialogAsMethodRedererProps>;
type State = {
    isRequesting: boolean;
    showCode: boolean;
    isTimeOut: boolean;
    waitingTime: number;
};

class _PhoneForm extends React.PureComponent<Props> {
    static defaultProps = {
        likeDialog: false,
        dialogHeader: 'Подтверждение телефона',
        phoneButtonText: 'Запросить код',
        codeButtonText: 'Активировать',
    };

    codeWaitingTime = 60;
    isUnmounted = false;

    state: State = {
        isRequesting: false,
        showCode: false,
        isTimeOut: false,
        waitingTime: this.codeWaitingTime,
    };

    constructor(props) {
        super(props);

        props.setSubmitHandler(this.handleSubmit);
        props.setValidation(this.validate);
    }

    componentWillUnmount() {
        this.isUnmounted = true;
    }

    validate = (values: Values) => {
        return Forms.Validation.validateSchema({
            values,
            validationSchema: this.getValidationSchema(),
        });
    };

    getValidationSchema = () => {
        const schema = this.state.showCode
            ? this.getSchemaForForCode()
            : this.getSchemaForPhone();
        return Forms.Validation.createSchema(schema);
    };

    getSchemaForPhone() {
        return {
            phone: Forms.Validation.phone()
                .required()
                .phoneCountry(),
        };
    }

    getSchemaForForCode() {
        return {
            phone: Forms.Validation.phone()
                .required()
                .phoneCountry(),
            code: Forms.Validation.Yup.number().required(),
        };
    }

    handleSubmit = async (values: Values) => {
        const {
            _formikParams: formikParams,
            onConfirmed,
            onClose,
            beforeConfirmed,
        } = this.props;

        this.setState({ isRequesting: true });
        beforeConfirmed && beforeConfirmed();

        try {
            if (!this.state.showCode) {
                await this.handlePhone(values);
                return;
            }

            await this.handleCode(values);
            await onConfirmed();
            onClose && onClose();
        } catch (errorMessages) {
            McsErrors.handleErrorMessagesInForm({
                errorMessages,
                formikParams,
            });
            this.setState({ isRequesting: false });
        }
    };

    async handlePhone(values: Values) {
        this.setState({ isRequesting: true });
        try {
            await this.props.phoneForward({ phone: values.phone });
            this.setState({
                showCode: true,
                isTimeOut: true,
            });
            this.props.onCodeSent && this.props.onCodeSent();
        } finally {
            this.setState({
                isRequesting: false,
            });
        }
        await this.startRepeatCodeTimer();
    }

    async handleCode(values: Values) {
        await this.props.codeConfirm(values as CodeParams);
    }

    handleRepeatCodeRequest = async () => {
        await this.handlePhone(this.props._formikParams.values);
    };

    async startRepeatCodeTimer() {
        let waitingTime = this.codeWaitingTime;

        while (waitingTime) {
            await asyncHelper.delay(1000);
            waitingTime--;

            if (this.isUnmounted) {
                break;
            } else {
                this.setState({ waitingTime });
            }
        }

        if (!this.isUnmounted) {
            this.setState({
                isTimeOut: false,
                waitingTime: this.codeWaitingTime,
            });
        }
    }

    renderRepeatCodeBlock() {
        const { isTimeOut, waitingTime, showCode } = this.state;

        if (!showCode) {
            return null;
        }

        return isTimeOut ? (
            <span className={styles.timerWrapper}>
                Запросить повторно через {waitingTime}
            </span>
        ) : (
            <ButtonLink
                disabled={this.getIsRequesting()}
                className={styles.repeatCodeButton}
                onClick={this.handleRepeatCodeRequest}
            >
                Запросить код повторно
            </ButtonLink>
        );
    }

    getIsRequesting() {
        return this.state.isRequesting || this.props.isRequesting || false;
    }

    renderPhoneForm() {
        const { showCode } = this.state;

        return (
            <div className={styles.fieldsRow}>
                {showCode ? (
                    <Forms.FieldInput
                        placeholder="Код подтверждения из СМС"
                        name="code"
                        type="number"
                        noSystemSpinner
                        disabled={this.getIsRequesting()}
                    />
                ) : (
                    <Forms.FieldInput
                        name="phone"
                        maskPattern="phone"
                        placeholder="+7 (123) 123-45-67"
                        cleanMaskedValue
                        disabled={this.getIsRequesting()}
                    />
                )}
            </div>
        );
    }

    renderLikeDialog(phoneForm: React.ReactNode) {
        const {
            isOpened,
            onClose,
            dialogDescription,
            dialogHeader,
        } = this.props;

        return (
            <Dialog
                header={dialogHeader}
                isRequesting={this.getIsRequesting()}
                open={isOpened}
                onCancel={onClose}
                accept={this.getButtonText()}
                formikParams={this.props._formikParams}
                cancel="Введу позже"
            >
                <div className={styles.dialogDescriptionWrapper}>
                    {dialogDescription}
                </div>
                {phoneForm}
                {this.renderRepeatCodeBlock()}
            </Dialog>
        );
    }

    getButtonText = () => {
        const { phoneButtonText, codeButtonText } = this.props;

        return this.state.showCode ? codeButtonText : phoneButtonText;
    };

    renderInline(phoneForm: React.ReactNode) {
        const { _formikParams: formikParams } = this.props;
        const buttonText = this.getButtonText();

        return (
            <Forms.Form
                onSubmit={formikParams.handleSubmit}
                error={formikParams.hasErrors}
                className={styles.phoneForm}
            >
                <div className={styles.formContentWrapper}>
                    {phoneForm}

                    <div className={styles.actionsWrapper}>
                        <Button
                            primary
                            type="submit"
                            className={styles.button}
                            loading={this.getIsRequesting()}
                            disabled={this.getIsRequesting()}
                        >
                            {buttonText}
                        </Button>
                        {this.renderRepeatCodeBlock()}
                    </div>
                </div>
            </Forms.Form>
        );
    }

    render() {
        const { likeDialog } = this.props;
        const phoneForm = this.renderPhoneForm();

        return likeDialog
            ? this.renderLikeDialog(phoneForm)
            : this.renderInline(phoneForm);
    }
}

export const PhoneVerification = connector(formikConnector(_PhoneForm));
